(() => {
  const text = (node) => `${node?.textContent || ''} ${node?.getAttribute?.('aria-label') || ''}`.replace(/\s+/g, ' ').trim().toLowerCase();

  const findControl = (root, labels, selector = 'button, a, [role="button"], [role="tab"], option') => {
    const controls = [...root.querySelectorAll(selector)];
    return controls.find((control) => labels.some((label) => text(control).includes(label)));
  };

  class LukzerProductReviews {
    constructor(section) {
      this.section = section;
      this.provider = section.querySelector('[data-review-provider]');
      this.observer = new MutationObserver(() => this.enhance());
      this.bind();
      this.enhance();
      this.observer.observe(this.provider, { childList: true, subtree: true });
    }

    bind() {
      this.section.querySelectorAll('[data-review-open]').forEach((button) => {
        button.addEventListener('click', () => this.openForm());
      });

      this.section.querySelectorAll('[data-review-filter]').forEach((button) => {
        button.addEventListener('click', () => this.filter(button.dataset.reviewFilter, button));
      });

      this.section.querySelector('[data-review-sort]')?.addEventListener('change', (event) => {
        this.sort(event.target.value);
      });
    }

    openForm() {
      const trigger = findControl(this.provider, ['write a review', 'click to review', 'add review', 'share your experience']);
      const firstStar = this.provider.querySelector('[aria-label*="star" i], [class*="star" i]');
      (trigger || firstStar)?.click();
      this.provider.classList.add('is-form-open');
      window.setTimeout(() => {
        const form = this.provider.querySelector('form');
        form?.querySelector('input:not([type="hidden"]), textarea, button')?.focus({ preventScroll: true });
      }, 180);
    }

    filter(value, source) {
      const labels = value === 'all'
        ? ['all']
        : value === 'photos'
          ? ['with photos', 'photos', 'media']
          : [`${value} star`, `${value}-star`];
      const native = findControl(this.provider, labels);
      native?.click();

      this.section.querySelectorAll('[data-review-filter]').forEach((button) => {
        const active = button.dataset.reviewFilter === value;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
      source?.focus({ preventScroll: true });
    }

    sort(value) {
      const labels = {
        recent: ['most recent', 'newest', 'recent'],
        highest: ['highest rated', 'highest rating'],
        lowest: ['lowest rated', 'lowest rating']
      }[value];
      const nativeSelect = [...this.provider.querySelectorAll('select')].find((select) => (
        [...select.options].some((option) => labels.some((label) => text(option).includes(label)))
      ));
      if (nativeSelect) {
        const option = [...nativeSelect.options].find((item) => labels.some((label) => text(item).includes(label)));
        nativeSelect.value = option.value;
        nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
      findControl(this.provider, labels)?.click();
    }

    enhanceStars() {
      const form = this.provider.querySelector('form');
      if (!form) return;
      const candidates = [...form.querySelectorAll('button, label, [role="radio"], [aria-label*="star" i], [class*="star" i]')];
      const stars = candidates.filter((candidate, index) => {
        if (!/star|rating/i.test(`${candidate.className || ''} ${candidate.getAttribute('aria-label') || ''}`)) return false;
        return !candidates.some((other, otherIndex) => otherIndex !== index && candidate.contains(other));
      }).slice(0, 5);
      if (stars.length < 5) return;

      stars.forEach((star, index) => {
        star.dataset.lkzFormStar = '';
        star.setAttribute('aria-label', `Select ${index + 1} out of 5 stars`);
        const preview = () => stars.forEach((item, itemIndex) => item.classList.toggle('is-preview', itemIndex <= index));
        star.onpointerenter = preview;
        star.onfocus = preview;
        star.addEventListener('click', () => {
          stars.forEach((item, itemIndex) => item.classList.toggle('is-selected', itemIndex <= index));
        }, { once: true });
      });
      const group = stars[0].parentElement;
      if (group && !group.dataset.lkzBound) {
        group.dataset.lkzBound = 'true';
        group.addEventListener('pointerleave', () => stars.forEach((star) => star.classList.remove('is-preview')));
      }
    }

    enhance() {
      this.provider.querySelectorAll('img').forEach((image) => {
        image.loading = 'lazy';
        image.decoding = 'async';
      });
      this.provider.querySelectorAll('video').forEach((video) => {
        video.preload = 'metadata';
      });

      const heading = [...this.provider.querySelectorAll('h1, h2, h3, [role="heading"]')]
        .find((node) => text(node) === 'reviews');
      if (heading) heading.hidden = true;

      this.hideNativeChrome();
      this.enhanceStars();

      this.provider.querySelectorAll('form').forEach((form) => {
        if (form.dataset.lkzSubmitGuard) return;
        form.dataset.lkzSubmitGuard = 'true';
        form.addEventListener('submit', () => {
          const submit = form.querySelector('[type="submit"]');
          if (!submit || submit.disabled) return;
          submit.disabled = true;
          submit.setAttribute('aria-busy', 'true');
          window.setTimeout(() => {
            submit.disabled = false;
            submit.removeAttribute('aria-busy');
          }, 8000);
        });
      });
    }

    hideNativeChrome() {
      if (Number.parseInt(this.section.dataset.reviewCount || '0', 10) > 0) {
        const summary = [...this.provider.querySelectorAll('div, section')]
          .filter((node) => {
            const progressCount = node.querySelectorAll('[role="progressbar"], [class*="progress" i]').length;
            const hasReviewAction = Boolean(findControl(node, ['write a review', 'click to review', 'add review', 'share your experience']));
            return progressCount >= 5 && hasReviewAction;
          })
          .sort((a, b) => a.querySelectorAll('*').length - b.querySelectorAll('*').length)[0];
        if (summary) summary.dataset.lkzProviderSummary = '';
      }

      this.provider.querySelectorAll('select').forEach((select) => {
        if (![...select.options].some((option) => /recent|highest|lowest|rating/i.test(text(option)))) return;
        const toolbar = select.closest('[class*="sort" i], [class*="filter" i]') || select.parentElement;
        if (toolbar) toolbar.dataset.lkzNativeToolbar = '';
      });
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll('[data-lkz-premium-reviews]').forEach((section) => {
      if (section.dataset.lkzInitialized) return;
      section.dataset.lkzInitialized = 'true';
      new LukzerProductReviews(section);
      const navLink = document.querySelector('.lkz-product-nav a[href="#product-reviews"]');
      if (navLink) navLink.href = `#${section.id}`;
    });
  };

  init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
